/* Ayudate · Capa de datos
   Único punto por donde la interfaz habla con el servidor. Ninguna pantalla
   hace fetch por su cuenta: todas piden acá, contra el Apps Script publicado,
   que lee y escribe en la hoja de cálculo maestra.

   La URL del script se toma de una variable de entorno para no dejarla
   escrita en el repositorio. En Cloudflare Pages se define en
   Settings › Environment variables con el nombre VITE_APPS_SCRIPT.
------------------------------------------------------------------- */

import { capitalizar, iniciales } from "./lib/formato";
import { franjasDe } from "./lib/horarios";
import { precioDe } from "./lib/tarifas";

const URL_SCRIPT = import.meta.env?.VITE_APPS_SCRIPT ?? "";

export const espera = (ms) => new Promise((r) => setTimeout(r, ms));

/* ------------------------------------------------------------------
   ERRORES CON MENSAJE LISTO PARA MOSTRAR
------------------------------------------------------------------- */
export class ErrorApi extends Error {
  constructor(mensaje, codigo) {
    super(mensaje);
    this.name = "ErrorApi";
    this.codigo = codigo;
  }
}

const MENSAJES = {
  red: "No pudimos conectarnos. Revise su internet y pruebe de nuevo.",
  servidor: "Algo falló de nuestro lado. Pruebe de nuevo en un momento.",
  datos: "Revise los datos: algo no está completo o no tiene el formato esperado.",
  tardo: "El envío está tardando demasiado (¿archivos pesados o conexión lenta?). "
    + "No lo sabemos con certeza del lado del servidor: espere un momento, revise su correo "
    + "por si ya llegó la confirmación, y si no llegó nada, pruebe de nuevo.",
};

/* Tiempo máximo esperando la respuesta de un envío (POST): registrar un
   tutor sube foto y comprobantes en base64 dentro del mismo cuerpo, así que
   puede tardar bastante más que una lectura común. Sin este límite, una
   conexión que se cae a mitad de subida deja el fetch() colgado para
   siempre — el estudiante se queda mirando la barra de carga sin ningún
   aviso, exactamente el "silencio" que este límite existe para evitar. */
const TIEMPO_ESPERA_MS = 45000;

/* ------------------------------------------------------------------
   LLAMADA AL APPS SCRIPT

   Ojo con el Content-Type: si se manda application/json, el navegador
   hace una petición previa de verificación que Apps Script no responde
   bien, y el envío falla en silencio. Con text/plain llega igual y
   funciona.
------------------------------------------------------------------- */
async function pedir(datos) {
  const limite = new AbortController();
  const vencido = setTimeout(() => limite.abort(), TIEMPO_ESPERA_MS);

  let r;
  try {
    r = await fetch(URL_SCRIPT, {
      method: "POST",
      headers: { "Content-Type": "text/plain;charset=utf-8" },
      body: JSON.stringify(datos),
      signal: limite.signal,
    });
  } catch (err) {
    throw new ErrorApi(err?.name === "AbortError" ? MENSAJES.tardo : MENSAJES.red,
      err?.name === "AbortError" ? "TIEMPO_AGOTADO" : "RED");
  } finally {
    clearTimeout(vencido);
  }
  if (!r.ok) throw new ErrorApi(MENSAJES.servidor, "SERVIDOR");

  const cuerpo = await r.json().catch(() => null);
  if (!cuerpo) throw new ErrorApi(MENSAJES.servidor, "SERVIDOR");
  if (cuerpo.ok === false) {
    throw new ErrorApi(cuerpo.mensaje || MENSAJES.datos, cuerpo.error || "DATOS");
  }
  return cuerpo;
}

/** Lee la hoja publicada. */
async function leerHoja(recurso) {
  let r;
  try {
    r = await fetch(`${URL_SCRIPT}?recurso=${recurso}`);
  } catch {
    throw new ErrorApi(MENSAJES.red, "RED");
  }
  if (!r.ok) throw new ErrorApi(MENSAJES.servidor, "SERVIDOR");
  return r.json();
}

/* ------------------------------------------------------------------
   NORMALIZACIÓN

   Deja cualquier tutor que venga de la hoja con la misma forma que
   esperan los componentes. Si mañana cambia una columna de la hoja,
   se ajusta solo acá.
------------------------------------------------------------------- */
function normalizarTutor(t) {
  const disponibilidad = typeof t.disponibilidad === "string"
    ? JSON.parse(t.disponibilidad || "{}")
    : (t.disponibilidad || {});
  const tarifasPorCurso = t.tarifasPorCurso
    || Object.fromEntries((t.cursos || []).map((c) => [c, t.tarifaBase]));
  const menor = Object.values(tarifasPorCurso).length
    ? Math.min(...Object.values(tarifasPorCurso)) : 0;
  const pr = precioDe(menor);

  return {
    ...t,
    nom: capitalizar(t.nom || t.nombre || ""),
    ini: t.ini || iniciales(t.nom || t.nombre || ""),
    cursos: Array.isArray(t.cursos) ? t.cursos : String(t.cursos || "").split(" | ").filter(Boolean),
    estado: t.estado || "aprobado",
    foto: t.foto || null,
    rating: t.rating ?? null,
    resenas: Number(t.resenas || 0),
    sesiones: Number(t.sesiones || 0),
    tarifasPorCurso,
    tarifaCliente: pr.cliente,
    tarifaTutor: pr.tutor,
    disponibilidad,
    franjas: franjasDe(disponibilidad),
  };
}

/* ==================================================================
   API PÚBLICA
================================================================== */
export const api = {
  /**
   * Listado de ofertas visibles en el buscador.
   * Solo llegan los CURSOS con publicado = "SI" Y estado = "aprobado" —
   * evaluado fila por fila, no por tutor: un tutor con varios cursos puede
   * tener unos visibles y otros no (pendiente, pausado o retirado) al mismo
   * tiempo, sin que uno afecte al otro.
   */
  async getTutores() {
    const datos = await leerHoja("tutores");
    return (datos.tutores || [])
      .map((t) => {
        const estadosPorCurso = t.estadosPorCurso || {};
        const publicadosPorCurso = t.publicadosPorCurso || {};
        const cursosVisibles = (t.cursos || []).filter((c) =>
          String(publicadosPorCurso[c] || "").trim().toUpperCase() === "SI"
          && String(estadosPorCurso[c] || "").trim() === "aprobado"
        );
        return cursosVisibles.length ? { ...t, cursos: cursosVisibles } : null;
      })
      .filter(Boolean)
      .map(normalizarTutor);
  },

  /**
   * Reseñas públicas de un tutor en un curso puntual, para el modal "Ver
   * reseñas" del catálogo (ver TutorCard.jsx). Los nombres de estudiante ya
   * llegan anonimizados desde el servidor — este recurso no pide token.
   */
  async getResenasPublicas(tutorId, curso) {
    const datos = await leerHoja(
      `resenas&tutorId=${encodeURIComponent(tutorId)}&curso=${encodeURIComponent(curso)}`
    );
    return datos.resenas || [];
  },

  /**
   * Registra un tutor. Entra siempre como "pendiente": no aparece en el
   * sitio público hasta que un administrador verifique su comprobante y lo
   * apruebe en la hoja — no hay actualización optimista acá, la tarjeta
   * solo existe una vez que el backend la devuelve como aprobada.
   * El servidor manda el correo de aviso al equipo y el acuse al tutor.
   */
  async crearTutor(datos) {
    /* La hoja guarda una fila por curso: mismos datos generales del tutor,
       con la tarifa, la descripción y la nota propias de cada oferta. Se arma
       acá para que Apps Script solo tenga que recorrer el arreglo e insertar.
       El comprobante viaja como imagen ya preparada (dataURL); Apps Script lo
       sube a Drive y guarda el enlace en "Datos privados" — nunca en la hoja
       pública de tutores. */
    const cursos = (datos.cursos || []).map((c) => ({
      curso: c,
      descripcion: (datos.descripciones || {})[c] || "",
      nota: (datos.notas || {})[c] || "",
      comprobante: (datos.comprobantes || {})[c] || "",
      tarifaBase: Number((datos.tarifasPorCurso || {})[c]) || 0,
    }));

    return pedir({
      tipo: "tutor",
      nombre: datos.nombre,
      correo: datos.correo,
      telefono: datos.telefono,
      carrera: datos.carrera,
      anio: datos.anio || "",
      disponibilidad: datos.disponibilidad || {},
      foto: datos.foto || "",
      cuentaTipo: datos.cuentaTipo || "",
      cuentaNumero: datos.cuentaNumero || "",
      cuentaTitular: datos.cuentaTitular || "",
      cursos,
      usuario: datos.usuario,
      password: datos.password,
    });
  },

  /** true si ese usuario está libre (mirando tutores y estudiantes a la
   *  vez), para el indicador en vivo del campo "Usuario" al registrarse. */
  async verificarUsuarioDisponible(usuario) {
    const r = await leerHoja(`usuarioDisponible&usuario=${encodeURIComponent(usuario)}`);
    return !!r.disponible;
  },

  /**
   * Solicitud de tutoría. El servidor guarda la fila, genera el token de
   * confirmación y le manda el correo al tutor con el enlace.
   * El estudiante NO paga en este paso.
   *
   * Traduce los nombres que usa la Agenda (tutor, sel, tel) a los que espera
   * apps-script.gs (tutorId, bloques, telefono): son los mismos datos, la
   * capa de datos es el único lugar donde se hace ese ajuste.
   */
  async crearSolicitud({ tutor, curso, sel, nombre, correo, tel, nota, sesionId }) {
    return pedir({
      tipo: "solicitud",
      tutorId: tutor,
      curso,
      bloques: sel,
      nombre,
      correo,
      telefono: tel,
      nota,
      sesionId,
    });
  },

  /**
   * Ocupación en vivo del calendario de un tutor+curso: qué bloques ya
   * tienen una solicitud viva (pendiente de tutor, confirmada o pagada) y
   * cuáles están bajo la reserva temporal de OTRO estudiante ahora mismo
   * (sesionId identifica la propia, para no bloquearse a uno mismo). La usa
   * Agenda.jsx para deshabilitar esos bloques desde el vamos, en vez de
   * dejarlos clickeables y recién avisar al enviar.
   */
  async getOcupacion(tutorId, curso, sesionId) {
    const datos = await leerHoja(
      `ocupacion&tutorId=${encodeURIComponent(tutorId)}`
      + `&curso=${encodeURIComponent(curso)}&sesionId=${encodeURIComponent(sesionId)}`
    );
    return { ocupado: datos.ocupado || {}, reservadoTemporal: datos.reservadoTemporal || {} };
  },

  /**
   * Registra o renueva, por 5 minutos, la reserva temporal de los bloques
   * que este estudiante tiene elegidos ahora mismo (sin haber enviado la
   * solicitud todavía). Bloques vacío la libera. No lanza ni bloquea la
   * interfaz si falla: es solo para que OTROS estudiantes vean el bloque
   * ocupado un poco antes; hayConflicto() en el servidor sigue siendo la
   * defensa real al enviar.
   */
  async reservarTemporal(tutorId, curso, bloques, sesionId) {
    return pedir({ tipo: "reservarTemporal", tutorId, curso, bloques, sesionId });
  },

  /** Confirma que el token de /calificar corresponde a una sesión PAGADA
   *  (dada, cobrada, todavía sin calificar) antes de mostrar el formulario. */
  async verificarToken(token) {
    return leerHoja(`calificacion&t=${encodeURIComponent(token)}`);
  },

  /** Guarda la calificación de una sesión. El servidor repite la misma
   *  verificación de arriba antes de escribir nada — este llamado no es
   *  la única puerta. */
  async calificar(token, datos) {
    return pedir({ tipo: "calificacion", token, ...datos });
  },

  /** Verificación previa de /pagar (ver SubirComprobante.jsx): si ese enlace
   *  todavía sirve y, si sirve, de qué tutoría es y cuánto plazo queda. El
   *  token es el de la columna tokenComprobante, el que viaja en el botón
   *  del correo de cobro — no el de sesión ni el del tutor. */
  async verificarComprobantePago(token) {
    return leerHoja(`comprobantePago&t=${encodeURIComponent(token)}`);
  },

  /** Sube el comprobante de SINPE del estudiante. `archivo` es la imagen ya
   *  preparada por prepararComprobante() — las mismas reglas que el
   *  comprobante de nota del tutor y la foto de perfil. El servidor repite
   *  todas las verificaciones antes de escribir nada: este llamado no es la
   *  única puerta. */
  async subirComprobantePago(token, archivo) {
    return pedir({ tipo: "subirComprobantePago", token, archivo });
  },

  /** Datos de la sala de una sesión pagada (ver SalaJitsi.jsx): el enlace
   *  crudo de Jitsi más el horario programado, para armar la ventana de
   *  acceso y la cuenta regresiva en el cliente. */
  async obtenerSala(token) {
    return leerHoja(`sala&t=${encodeURIComponent(token)}`);
  },

  /** Avisa al servidor que un usuario efectivamente entró a la videollamada
   *  (evento "videoConferenceJoined" del SDK de Jitsi, ver SalaJitsi.jsx):
   *  el servidor agrega una fila a la bitácora de entradas con el nombre de
   *  sala, quién entró, su rol y el momento exacto. Es solo una bitácora,
   *  no una puerta de acceso — no bloquea la interfaz si falla. */
  async registrarEntradaSala(token, { roomName, nombre, rol, entradaEn }) {
    return pedir({ tipo: "entradaSala", token, roomName, nombre, rol, entradaEn });
  },

  /** Avisa al servidor que se cumplió la tolerancia de salida de la sala:
   *  completa la sesión (COMPLETADA, sesión sumada al tutor, correo de
   *  calificación) sin esperar al barrido del minuto siguiente. Reentrante
   *  — no falla si la sesión ya estaba completada. */
  async completarSesion(token) {
    return pedir({ tipo: "completarSesion", token });
  },

  /* ----------------------------------------------------------------
     LOGIN ÚNICO (ver Login.jsx)
     Un solo usuario+contraseña para los dos paneles: el servidor prueba
     contra tutores y contra estudiantes, y devuelve con cuál rol
     coincidió, para que la pantalla sepa a cuál dashboard mandar.
  ---------------------------------------------------------------- */

  /** Devuelve { token, rol: "tutor"|"estudiante", idTutor|correo, nombre? }
   *  además del ok, según con qué cuenta haya coincidido. */
  async iniciarSesion(username, password) {
    return pedir({ tipo: "loginUnificado", username, password });
  },

  /* ----------------------------------------------------------------
     DASHBOARD DEL TUTOR
     El "token" de acá abajo es siempre el de sesión del login (ver
     sesionTutor.js) — las credenciales se eligen directo en FormTutor.jsx
     al registrarse, ya no con un token de invitación aparte.
  ---------------------------------------------------------------- */

  /** "Olvidé mi contraseña" desde /login: pide el enlace de recuperación
   *  para el correo registrado del tutor. El servidor SIEMPRE responde con
   *  el mismo mensaje genérico, exista o no ese correo — nunca hay que usar
   *  esta respuesta para decidir si el correo está registrado. */
  async solicitarRecuperacionPassword(email) {
    return pedir({ tipo: "solicitarRecuperacionPassword", email });
  },

  /** Cierra "olvidé mi contraseña": el token viene de /restablecer-password
   *  (el enlace del correo que mandó solicitarRecuperacionPassword). */
  async confirmarRestablecimientoPassword(token, nuevaPassword) {
    return pedir({ tipo: "confirmarRestablecimientoPassword", token, nuevaPassword });
  },

  /** Todo lo que arma el dashboard: cursos del tutor, sus próximas
   *  reuniones ya pagadas con el enlace de Jitsi, y su historial de
   *  reuniones pasadas o completadas. */
  async obtenerDashboardTutor(sessionToken) {
    return pedir({ tipo: "dashboardTutor", token: sessionToken });
  },

  /** Cambia la contraseña del tutor autenticado, verificando la actual. */
  async cambiarPasswordTutor(sessionToken, actual, nueva) {
    return pedir({ tipo: "cambiarPasswordTutor", token: sessionToken, actual, nueva });
  },

  /** Cambia el correo del tutor autenticado, verificando la contraseña
   *  actual. Devuelve { correo } con el correo nuevo ya normalizado. */
  async cambiarCorreoTutor(sessionToken, actual, nuevoCorreo) {
    return pedir({ tipo: "cambiarCorreoTutor", token: sessionToken, actual, nuevoCorreo });
  },

  /** Reporta un incidente desde el propio dashboard del tutor autenticado:
   *  idTutor/tutorNombre los toma el servidor de la sesión, nunca de acá.
   *  tipoIncidente (no "tipo": ver la nota de crearIncidenteAdmin en
   *  apps-script.gs) es uno de "queja"/"tecnico"/"otro" — sin "ausencia",
   *  que no tiene sentido que un tutor se reporte a sí mismo. */
  async crearIncidenteTutor(sessionToken, { curso, estudiante, tipoIncidente, descripcion }) {
    return pedir({ tipo: "crearIncidenteTutor", token: sessionToken,
      curso, estudiante, tipoIncidente, descripcion });
  },

  /* ----------------------------------------------------------------
     PANEL DEL ESTUDIANTE
     Mismo patrón que el bloque de arriba, mirando sesionEstudiante.js en
     vez de sesionTutor.js. La cuenta se crea directo en
     CrearCuentaEstudiante.jsx, sin token de invitación — completamente
     aparte de reservar una tutoría en Agenda.jsx, que sigue siendo anónimo.
  ---------------------------------------------------------------- */

  /** Alta directa de cuenta de estudiante: nombre/correo/teléfono más el
   *  usuario y contraseña elegidos en el mismo formulario. */
  async registrarEstudiante({ nombre, correo, telefono, usuario, password }) {
    return pedir({ tipo: "estudiante", nombre, correo, telefono, usuario, password });
  },

  /** "Olvidé mi contraseña" desde /login (ver Login.jsx) — mismo mensaje
   *  genérico siempre, exista o no ese correo. */
  async solicitarRecuperacionPasswordEstudiante(email) {
    return pedir({ tipo: "solicitarRecuperacionPasswordEstudiante", email });
  },

  /** Cierra "olvidé mi contraseña" con el token de /restablecer-password-estudiante. */
  async confirmarRestablecimientoPasswordEstudiante(token, nuevaPassword) {
    return pedir({ tipo: "confirmarRestablecimientoPasswordEstudiante", token, nuevaPassword });
  },

  /** Próximas reuniones (con enlace a la sala) e historial de reuniones
   *  pasadas/completadas del estudiante autenticado, más su nombre/teléfono
   *  guardados para precargar "Solicitar nueva tutoría". */
  async obtenerDashboardEstudiante(sessionToken) {
    return pedir({ tipo: "dashboardEstudiante", token: sessionToken });
  },

  /** Cambia la contraseña del estudiante autenticado, verificando la actual. */
  async cambiarPasswordEstudiante(sessionToken, actual, nueva) {
    return pedir({ tipo: "cambiarPasswordEstudiante", token: sessionToken, actual, nueva });
  },

  /** Cambia el correo del estudiante autenticado, verificando la contraseña
   *  actual. Devuelve { correo } con el correo nuevo ya normalizado, para
   *  que la pantalla actualice la sesión guardada (ver sesionEstudiante.js). */
  async cambiarCorreoEstudiante(sessionToken, actual, nuevoCorreo) {
    return pedir({ tipo: "cambiarCorreoEstudiante", token: sessionToken, actual, nuevoCorreo });
  },

  /** Reporta un incidente desde el propio panel del estudiante autenticado:
   *  el campo "estudiante" lo toma el servidor de la sesión, nunca de acá.
   *  tipoIncidente es uno de "ausencia"/"tecnico"/"otro" — sin "queja", que
   *  no tiene sentido que un estudiante se reporte una queja a sí mismo. */
  async crearIncidenteEstudiante(sessionToken, { tutorNombre, curso, tipoIncidente, descripcion }) {
    return pedir({ tipo: "crearIncidenteEstudiante", token: sessionToken,
      tutorNombre, curso, tipoIncidente, descripcion });
  },

  /** Cancela una tutoría PAGADA del tutor autenticado, con el motivo que
   *  escribió (ver cancelarSesionTutor en apps-script.gs, sección 6f bis).
   *  `tokenSolicitud` es el token de esa reunión, no el de sesión. Al
   *  cancelar el tutor, al estudiante se le devuelve el 100%. */
  async cancelarSesionTutor(sessionToken, tokenSolicitud, motivo) {
    return pedir({ tipo: "cancelarSesionTutor", token: sessionToken, tokenSolicitud, motivo });
  },

  /** Lo mismo desde el panel del estudiante autenticado. Acá sí aplica la
   *  tabla de la política publicada: 80% de vuelta con más de 24 horas de
   *  aviso, 60% con 24 horas o menos. */
  async cancelarSesionEstudiante(sessionToken, tokenSolicitud, motivo) {
    return pedir({ tipo: "cancelarSesionEstudiante", token: sessionToken, tokenSolicitud, motivo });
  },

  /** "Puedo dar la clase" / "No puedo dar la clase" sobre una solicitud
   *  PENDIENTE_TUTOR del tutor autenticado (ver datos.solicitudesPendientes
   *  en obtenerDashboardTutor) — mismo camino único de escritura que el
   *  enlace del correo, solo que sin salir del dashboard. `accion` es
   *  "confirmar" o "rechazar"; `tokenSolicitud` es el token de esa fila
   *  (no el de sesión).
   *
   *  `mensaje` solo va al rechazar, y es obligatorio: es lo que el
   *  estudiante recibe por correo explicando por qué no se puede a esa hora
   *  —o a cuál sí— para que vuelva a pedirla (ver RechazarSolicitud.jsx y
   *  responderSolicitudTutor en apps-script.gs, que lo vuelve a validar). */
  async responderSolicitudTutor(sessionToken, tokenSolicitud, accion, mensaje) {
    return pedir({ tipo: "responderSolicitudTutor", token: sessionToken, tokenSolicitud,
                   accion, mensaje });
  },

  /** Edita la descripción de un curso puntual del tutor autenticado. */
  async editarDescripcionCurso(sessionToken, curso, descripcion) {
    return pedir({ tipo: "editarDescripcionCurso", token: sessionToken, curso, descripcion });
  },

  /** Cambia la tarifa por hora de un curso puntual del tutor autenticado. */
  async cambiarTarifaCurso(sessionToken, curso, nuevaTarifa) {
    return pedir({ tipo: "cambiarTarifaCurso", token: sessionToken, curso, nuevaTarifa });
  },

  /** Publica u oculta UN curso puntual del tutor autenticado, sin tocar su
   *  estado de aprobación. */
  async cambiarPublicadoCurso(sessionToken, curso, publicado) {
    return pedir({ tipo: "cambiarPublicadoCurso", token: sessionToken, curso, publicado });
  },

  /** Retira o reactiva UN curso puntual del tutor autenticado
   *  (nuevoEstado: "retirado" o "aprobado"). */
  async cambiarEstadoCurso(sessionToken, curso, nuevoEstado) {
    return pedir({ tipo: "cambiarEstadoCurso", token: sessionToken, curso, nuevoEstado });
  },

  /** Reemplaza la disponibilidad del tutor autenticado (misma forma que
   *  produce Grilla) en TODAS sus filas de "Tutores" a la vez. */
  async cambiarDisponibilidadTutor(sessionToken, disponibilidad) {
    return pedir({ tipo: "cambiarDisponibilidadTutor", token: sessionToken, disponibilidad });
  },

  /** Sube una foto de perfil nueva (dataURL ya preparada con prepararFoto())
   *  y la aplica a TODAS las filas de "Tutores" del tutor autenticado. */
  async cambiarFotoTutor(sessionToken, foto) {
    return pedir({ tipo: "cambiarFotoTutor", token: sessionToken, foto });
  },

  /** Agrega UN curso nuevo al tutor autenticado, reutilizando su nombre,
   *  carrera, año, foto y disponibilidad ya cargados — solo pide lo propio
   *  del curso. Entra como "pendiente" (ver agregarCursoTutor), igual que
   *  un curso del registro inicial. */
  async agregarCursoTutor(sessionToken, curso, nota, comprobante, tarifaBase, descripcion) {
    return pedir({
      tipo: "agregarCursoTutor", token: sessionToken,
      curso, nota, comprobante, tarifaBase, descripcion,
    });
  },

  /* ----------------------------------------------------------------
     PANEL DE ADMINISTRACIÓN (/admin)
     El "token" de acá abajo es la sesión de admin (ver sesionAdmin.js),
     una sola contraseña compartida por todo el equipo — no hay usuario
     por persona, ver CONFIG.claveAdminHash en apps-script.gs.
  ---------------------------------------------------------------- */

  /** Login del panel de administración. Devuelve { token } además del ok. */
  async iniciarSesionAdmin(password) {
    return pedir({ tipo: "loginAdmin", password });
  },

  /** Cursos/tutores pendientes de revisión, con lo necesario para
   *  aprobarlos o rechazarlos sin abrir la hoja de cálculo. */
  async obtenerColaAprobacion(sessionToken) {
    return pedir({ tipo: "panelAdmin", token: sessionToken });
  },

  /** Aprueba un curso pendiente: lo publica y le avisa al tutor. */
  async aprobarCursoAdmin(sessionToken, idTutor, curso) {
    return pedir({ tipo: "aprobarCursoAdmin", token: sessionToken, idTutor, curso });
  },

  /** Rechaza un curso pendiente: no se publica ni avisa nada automático. */
  async rechazarCursoAdmin(sessionToken, idTutor, curso) {
    return pedir({ tipo: "rechazarCursoAdmin", token: sessionToken, idTutor, curso });
  },

  /** Desglose semanal (lunes a domingo) de lo cobrado por SINPE, la
   *  comisión de Ayudate y lo que le toca a cada tutor, con el estado de
   *  liquidación de cada semana. Ver la sección 11 de apps-script.gs. */
  async obtenerFinanzasAdmin(sessionToken) {
    return pedir({ tipo: "finanzasAdmin", token: sessionToken });
  },

  /** Marca (o desmarca) una semana+tutor como ya liquidada. No
   *  mueve plata: solo guarda que la transferencia bancaria ya se hizo a mano.
   *  Al marcar (liquidado=true), `comprobante` (dataURL, ya preparado con
   *  prepararComprobante()) es obligatorio — el servidor lo rechaza sin
   *  eso (ver marcarLiquidacionAdmin en apps-script.gs); al desmarcar no
   *  hace falta. */
  async marcarLiquidacionAdmin(sessionToken, semana, idTutor, tutorNombre, liquidado, comprobante) {
    return pedir({
      tipo: "marcarLiquidacionAdmin", token: sessionToken,
      semana, idTutor, tutorNombre, liquidado, comprobante,
    });
  },

  /** Solicitudes confirmadas con comprobante de SINPE ya detectado,
   *  esperando que un admin las apruebe o rechace — la misma cola que
   *  también se puede resolver desde los botones del correo (esos sí
   *  con una ventana de gracia de unos minutos para deshacer un clic
   *  accidental). */
  async obtenerPagosPendientesAdmin(sessionToken) {
    return pedir({ tipo: "pagosPendientesAdmin", token: sessionToken });
  },

  /** Aprueba o rechaza, desde el panel, un comprobante de la cola de
   *  arriba. A diferencia de los botones del correo, esto lo aplica al
   *  instante — sala asignada y correos de confirmación (o rechazo) ya
   *  mismo, ver decidirPagoAdmin en apps-script.gs. */
  async decidirPagoAdmin(sessionToken, tokenPago, accion) {
    return pedir({ tipo: "decidirPagoAdmin", token: sessionToken, tokenPago, accion });
  },

  /** Suspende TODA la cuenta de un tutor (todos sus cursos aprobados
   *  pasan a "suspendido" y se despublican) por incumplir políticas. */
  async suspenderTutorAdmin(sessionToken, idTutor) {
    return pedir({ tipo: "suspenderTutorAdmin", token: sessionToken, idTutor });
  },

  /** Deshace una suspensión: los cursos "suspendido" de ese tutor
   *  vuelven a "aprobado" y publicado. */
  async reactivarTutorAdmin(sessionToken, idTutor) {
    return pedir({ tipo: "reactivarTutorAdmin", token: sessionToken, idTutor });
  },

  /** Roster completo (todos los tutores, en cualquier estado) con su
   *  contacto privado — para el control de estado y la exportación CSV. */
  async obtenerRosterTutoresAdmin(sessionToken) {
    return pedir({ tipo: "rosterTutoresAdmin", token: sessionToken });
  },

  /** Puntualidad de cada tutor (a tiempo vs. tardías), calculada a partir
   *  de las sesiones ya calificadas — ver obtenerPuntualidadAdmin en
   *  apps-script.gs. Es de solo lectura y no manda ningún correo. */
  async obtenerPuntualidadAdmin(sessionToken) {
    return pedir({ tipo: "puntualidadAdmin", token: sessionToken });
  },

  /** Últimas entradas de la bitácora de actividad administrativa (cursos
   *  aprobados/rechazados, pagos marcados como liquidados) — de solo
   *  lectura, ver la sección 10 de apps-script.gs. */
  async obtenerBitacoraAdmin(sessionToken) {
    return pedir({ tipo: "bitacoraAdmin", token: sessionToken });
  },

  /** Reporta un incidente nuevo (queja, ausencia, falla técnica…) desde el
   *  panel admin. Solo `descripcion` es obligatoria — ver la sección 16 de
   *  apps-script.gs. */
  async crearIncidenteAdmin(sessionToken, datos) {
    return pedir({ tipo: "crearIncidenteAdmin", token: sessionToken, ...datos });
  },

  /** Lista completa de incidentes reportados, más reciente primero. */
  async obtenerIncidentesAdmin(sessionToken) {
    return pedir({ tipo: "incidentesAdmin", token: sessionToken });
  },

  /** Marca un incidente como resuelto, o lo reabre (nuevoEstado:
   *  "resuelto" o "abierto"). */
  async cambiarEstadoIncidenteAdmin(sessionToken, id, nuevoEstado) {
    return pedir({ tipo: "cambiarEstadoIncidenteAdmin", token: sessionToken, id, nuevoEstado });
  },
};
